import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Plus, Lock, Pencil, Trash2, Search, Mail, Tag, ListTree, Eye, X, Loader2,
  ShieldCheck, GitBranch, Info,
} from "lucide-react";

const STAGE_BADGE = {
  APPLYING: "bg-amber-100 text-amber-700",
  FOLLOW_UP: "bg-sky-100 text-sky-700",
  RECRUITER_REPLIED: "bg-violet-100 text-violet-700",
};
const SUB_BADGE = "bg-slate-100 text-slate-600";

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} shadow-2xl flex flex-col max-h-[92vh]`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30";

function ResponseBadge({ p }) {
  if (!p || (!p.sent && !p.later_sends)) {
    return <span className="text-xs text-slate-300" title="Not used yet">—</span>;
  }
  const rate = p.reply_rate;
  const cls = rate == null ? "bg-slate-100 text-slate-500 border-slate-200"
    : rate >= 30 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : rate > 0 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-100 text-slate-500 border-slate-200";
  const label = rate == null ? `${p.later_sends}× used` : `${rate}% reply`;
  const title = p.sent
    ? `${p.replied} replied · ${p.opened} opened · ${p.sent} sent${p.later_sends ? ` · ${p.later_sends} follow-up/reply` : ""}`
    : `Used ${p.later_sends}× as a follow-up/reply`;
  return (
    <span title={title} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

export default function AdminEmailTemplates() {
  const [overview, setOverview] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [perf, setPerf] = useState({});
  const [activeCat, setActiveCat] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);       // template editor
  const [catModal, setCatModal] = useState(false);   // new category
  const [subModal, setSubModal] = useState(false);   // manage subcategories
  const [ruleModal, setRuleModal] = useState(false); // edit scenario rule
  const [previewId, setPreviewId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ov }, { data: tpl }, { data: vars }, { data: performance }] = await Promise.all([
        api.get("/admin/comm/overview"),
        api.get("/admin/templates"),
        api.get("/admin/templates/variables"),
        api.get("/admin/templates/performance"),
      ]);
      setOverview(ov);
      setTemplates(tpl.items);
      setVariables(vars.items || vars.variables || []);
      setPerf(Object.fromEntries((performance.items || []).map((p) => [p.id, p])));
      setActiveCat((prev) => prev || ov.categories[0]?.key);
    } catch (e) {
      toast.error("Could not load template management.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cat = overview?.categories.find((c) => c.key === activeCat);
  const rule = overview?.scenario_rules.find((r) => r.stage === cat?.stage);
  const catTemplates = useMemo(
    () => templates.filter((t) => t.category_key === activeCat &&
      (!search.trim() || (t.name + t.subject).toLowerCase().includes(search.toLowerCase()))),
    [templates, activeCat, search]);
  const subName = (key) => cat?.subcategories.find((s) => s.key === key)?.name || key;

  const toggleActive = async (t) => {
    try {
      await api.put(`/admin/templates/${t.id}`, { ...t, is_active: !t.is_active });
      setTemplates((ts) => ts.map((x) => x.id === t.id ? { ...x, is_active: !x.is_active } : x));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/admin/templates/${t.id}`); toast.success("Template deleted."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const previewTemplate = templates.find((t) => t.id === previewId) || catTemplates[0];

  if (loading || !overview) {
    return <div className="py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-500" /></div>;
  }

  return (
    <div data-testid="admin-templates-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Admin Email Template Management</h1>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-100 text-amber-700 text-xs font-semibold"><ShieldCheck className="w-3.5 h-3.5" /> Admin Controls</span>
          </div>
          <p className="text-slate-500 mt-1.5">Create reply categories, subcategories, scenario rules, and reusable templates for candidates.</p>
        </div>
        <div className="flex items-center gap-2.5">
          <Button data-testid="new-category-btn" variant="outline" onClick={() => setCatModal(true)} className="border-slate-200"><Plus className="w-4 h-4 mr-1.5" /> New Category</Button>
          <Button data-testid="new-template-btn" onClick={() => setEditor({ category_key: activeCat })} className="bg-amber-500 text-black hover:bg-amber-400 font-semibold"><Plus className="w-4 h-4 mr-1.5" /> New Template</Button>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2" data-testid="category-tabs">
        {overview.categories.map((c) => (
          <button key={c.key} data-testid={`cat-tab-${c.key}`} onClick={() => { setActiveCat(c.key); setPreviewId(null); }}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              c.key === activeCat ? "bg-amber-500 text-black border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"
            }`}>{c.name} ({c.template_count})</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5 items-start">
        <div className="space-y-5">
          {/* Category & Subcategories */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-slate-900">Category &amp; Subcategories</h2>
                <p className="text-sm text-slate-500 mt-0.5">Manage the category and its subcategories for this scenario.</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${STAGE_BADGE[cat?.stage] || "bg-slate-100 text-slate-600"}`}>
                {rule?.category_mode === "fixed" ? <Lock className="w-3.5 h-3.5" /> : <GitBranch className="w-3.5 h-3.5" />}
                {rule?.stage_label} scenario
              </span>
            </div>

            <div className="mt-4">
              <p className="text-xs text-slate-500 mb-1.5">Category {rule?.fixed_category_key === cat?.key ? "(Fixed)" : ""}</p>
              <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                {rule?.fixed_category_key === cat?.key ? <Lock className="w-4 h-4 text-slate-400" /> : <Tag className="w-4 h-4 text-slate-400" />}
                <span className="font-semibold text-slate-800">{cat?.name}</span>
                <span title="Category behaviour is governed by the scenario rule" className="ml-1"><Info className="w-3.5 h-3.5 text-slate-300" /></span>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">Subcategories</p>
              <Button data-testid="manage-subcategories-btn" variant="outline" size="sm" onClick={() => setSubModal(true)} className="border-slate-200"><Plus className="w-3.5 h-3.5 mr-1" /> Manage Subcategories</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mt-2.5" data-testid="subcategory-list">
              {(cat?.subcategories || []).map((s) => (
                <div key={s.key} className={`rounded-xl border px-3 py-2.5 ${s.active ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`}>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-semibold text-slate-800 truncate flex-1">{s.name}</span>
                    <span className="text-xs font-bold text-slate-400">{s.template_count}</span>
                  </div>
                </div>
              ))}
              {(cat?.subcategories || []).length === 0 && <p className="text-sm text-slate-400 col-span-full">No subcategories yet.</p>}
            </div>
          </div>

          {/* Templates */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100">
              <div><h2 className="font-bold text-slate-900">Templates</h2>
                <p className="text-sm text-slate-500">Candidates see these when they select a subcategory.</p></div>
              <div className="relative ml-auto">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="template-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates…" className="h-10 w-48 pl-9 pr-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-500" />
              </div>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="px-5 py-2.5 font-semibold">Template Name</th>
                <th className="px-3 py-2.5 font-semibold">Subcategory</th>
                <th className="px-3 py-2.5 font-semibold hidden md:table-cell">Subject Preview</th>
                <th className="px-3 py-2.5 font-semibold" title="Share of applications using this template that got a recruiter reply">Response</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {catTemplates.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No templates in this category.</td></tr>
                ) : catTemplates.map((t) => (
                  <tr key={t.id} data-testid={`template-row-${t.id}`} className="hover:bg-slate-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <Mail className="w-4 h-4 text-amber-500 shrink-0" />
                        <div><p className="font-semibold text-slate-900">{t.name}</p></div>
                      </div>
                    </td>
                    <td className="px-3 py-3"><span className={`text-[11px] font-semibold rounded px-2 py-0.5 ${SUB_BADGE}`}>{subName(t.subcategory_key)}</span></td>
                    <td className="px-3 py-3 text-slate-500 hidden md:table-cell truncate max-w-[240px]">{t.subject}</td>
                    <td className="px-3 py-3" data-testid={`template-response-${t.id}`}><ResponseBadge p={perf[t.id]} /></td>
                    <td className="px-3 py-3">
                      <button data-testid={`template-toggle-${t.id}`} onClick={() => toggleActive(t)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${t.is_active ? "bg-amber-500" : "bg-slate-300"}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${t.is_active ? "translate-x-4" : "translate-x-0.5"}`} />
                      </button>
                      <span className={`ml-2 text-xs font-semibold ${t.is_active ? "text-emerald-600" : "text-slate-400"}`}>{t.is_active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`template-edit-${t.id}`} onClick={() => setEditor(t)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`template-delete-${t.id}`} onClick={() => deleteTemplate(t)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-slate-100 text-sm text-slate-500">Showing {catTemplates.length} template(s)</div>
          </div>
        </div>

        {/* Right rail: scenario rules + preview */}
        <div className="space-y-5 lg:sticky lg:top-24">
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="scenario-rules-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-slate-900 flex items-center gap-1.5">Scenario Rules <Info className="w-3.5 h-3.5 text-slate-300" /></h2>
              <Button data-testid="edit-rule-btn" variant="ghost" size="sm" onClick={() => setRuleModal(true)} className="text-amber-600 hover:text-amber-700 h-auto py-1">Edit</Button>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">How categories and templates work for this scenario.</p>

            <div className="mt-3 space-y-3">
              <div className="rounded-xl border border-slate-200 px-4 py-3">
                <p className="text-[11px] text-slate-400">Scenario</p>
                <p className="font-semibold text-slate-900">{rule?.stage_label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{rule?.trigger_description}</p>
              </div>
              <div className="rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-2.5">
                {rule?.category_mode === "fixed" ? <Lock className="w-4 h-4 text-slate-400" /> : <GitBranch className="w-4 h-4 text-slate-400" />}
                <div><p className="text-[11px] text-slate-400">Category Mode</p>
                  <p className="font-semibold text-slate-900 capitalize">{rule?.category_mode === "fixed" ? "Fixed Category" : "Selectable"}</p></div>
              </div>
              <div className="rounded-xl border border-slate-200 px-4 py-3">
                <p className="text-[11px] text-slate-400 mb-1.5 flex items-center gap-1"><ListTree className="w-3.5 h-3.5" /> Allowed Categories</p>
                <div className="flex flex-wrap gap-1.5">
                  {(rule?.allowed_category_keys || []).map((k) => (
                    <span key={k} className="text-[11px] font-semibold rounded px-2 py-0.5 bg-emerald-50 text-emerald-700">
                      {overview.categories.find((c) => c.key === k)?.name || k}
                    </span>
                  ))}
                </div>
              </div>

              {(() => {
                const catPerf = templates
                  .filter((t) => (rule?.allowed_category_keys || []).includes(t.category_key))
                  .map((t) => perf[t.id]).filter((p) => p && p.sent > 0 && p.reply_rate != null);
                if (!catPerf.length) return null;
                const best = [...catPerf].sort((a, b) => b.reply_rate - a.reply_rate)[0];
                const avg = Math.round(catPerf.reduce((s, p) => s + p.reply_rate, 0) / catPerf.length);
                return (
                  <div data-testid="scenario-insight" className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3">
                    <p className="text-[11px] text-amber-600 font-semibold mb-1">Playbook insight</p>
                    <p className="text-sm text-slate-800">
                      <span className="font-semibold text-emerald-700">{best.name}</span> leads replies at{" "}
                      <span className="font-semibold">{best.reply_rate}%</span>
                      {" "}(avg {avg}% across this scenario).
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1">Refine or retire low performers; double down on the wording that lands replies.</p>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="template-preview-panel">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-bold text-slate-900 flex items-center gap-1.5"><Eye className="w-4 h-4 text-slate-400" /> Template Preview</h2>
              <select value={previewTemplate?.id || ""} onChange={(e) => setPreviewId(e.target.value)}
                className="h-9 rounded-lg border border-slate-200 px-2 text-xs text-slate-600 outline-none focus:border-amber-500 max-w-[150px]">
                {catTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Preview how this template looks to candidates.</p>
            {previewTemplate ? (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-200 bg-white"><p className="text-sm font-semibold text-slate-800">Subject: {previewTemplate.subject}</p></div>
                <div className="px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">{previewTemplate.body}</div>
              </div>
            ) : <p className="text-sm text-slate-400 mt-3">No template selected.</p>}
          </div>
        </div>
      </div>

      {editor && <TemplateEditor initial={editor} overview={overview} variables={variables}
        onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
      {catModal && <CategoryModal onClose={() => setCatModal(false)} onSaved={() => { setCatModal(false); load(); }} />}
      {subModal && cat && <SubcategoryModal category={cat} onClose={() => setSubModal(false)} onSaved={load} />}
      {ruleModal && rule && <RuleModal rule={rule} categories={overview.categories} onClose={() => setRuleModal(false)} onSaved={() => { setRuleModal(false); load(); }} />}
    </div>
  );
}

function TemplateEditor({ initial, overview, variables, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial.name || "", category_key: initial.category_key || overview.categories[0]?.key,
    subcategory_key: initial.subcategory_key || "", subject: initial.subject || "", body: initial.body || "",
    is_active: initial.is_active ?? true, candidate_visible: initial.candidate_visible ?? true,
    is_default: initial.is_default ?? false,
  });
  const [saving, setSaving] = useState(false);
  const cat = overview.categories.find((c) => c.key === form.category_key);
  const subs = cat?.subcategories || [];
  useEffect(() => {
    if (!subs.find((s) => s.key === form.subcategory_key)) setForm((f) => ({ ...f, subcategory_key: subs[0]?.key || "" }));
  }, [form.category_key]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim() || !form.subcategory_key) {
      toast.error("Name, subcategory, subject and body are required."); return;
    }
    setSaving(true);
    const payload = {
      name: form.name, category: cat?.name || "General", category_key: form.category_key,
      subcategory_key: form.subcategory_key, subject: form.subject, body: form.body,
      template_type: cat?.stage === "APPLYING" ? "application" : "reply",
      is_active: form.is_active, candidate_visible: form.candidate_visible, is_default: form.is_default,
    };
    try {
      if (initial.id) await api.put(`/admin/templates/${initial.id}`, payload);
      else await api.post("/admin/templates", payload);
      toast.success(initial.id ? "Template updated." : "Template created.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial.id ? "Edit Template" : "New Template"} onClose={onClose} wide>
      <div className="space-y-4" data-testid="template-editor">
        <div><label className="text-sm text-slate-700">Template Name</label>
          <input data-testid="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm text-slate-700">Category</label>
            <select data-testid="tpl-category" value={form.category_key} onChange={(e) => setForm({ ...form, category_key: e.target.value })} className={inputCls}>
              {overview.categories.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
            </select></div>
          <div><label className="text-sm text-slate-700">Subcategory</label>
            <select data-testid="tpl-subcategory" value={form.subcategory_key} onChange={(e) => setForm({ ...form, subcategory_key: e.target.value })} className={inputCls}>
              {subs.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select></div>
        </div>
        <div><label className="text-sm text-slate-700">Subject</label>
          <input data-testid="tpl-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} /></div>
        <div><label className="text-sm text-slate-700">Body</label>
          <textarea data-testid="tpl-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={9}
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30" /></div>
        <div className="text-[11px] text-slate-400">Merge fields: {(variables || []).map((v) => v.key ? `{${v.key}}` : v).slice(0, 12).join(", ")}</div>
        <div className="flex items-center gap-5 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.candidate_visible} onChange={(e) => setForm({ ...form, candidate_visible: e.target.checked })} /> Candidate visible</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Default</label>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-600">Cancel</Button>
          <Button data-testid="tpl-save" onClick={save} disabled={saving} className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-6">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Template"}</Button>
        </div>
      </div>
    </Modal>
  );
}

function CategoryModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try { await api.post("/admin/comm/categories", { name }); toast.success("Category created."); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New Category" onClose={onClose}>
      <div className="space-y-4" data-testid="category-modal">
        <div><label className="text-sm text-slate-700">Category Name</label>
          <input data-testid="cat-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Negotiation" /></div>
        <p className="text-[11px] text-slate-400">New categories are unlocked at a stage by adding them to a Scenario Rule.</p>
        <div className="flex justify-end gap-3"><Button variant="ghost" onClick={onClose} className="text-slate-600">Cancel</Button>
          <Button data-testid="cat-save" onClick={save} disabled={saving} className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-6">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}</Button></div>
      </div>
    </Modal>
  );
}

function SubcategoryModal({ category, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/comm/subcategories", { category_key: category.key, name, description: desc });
      toast.success("Subcategory added."); setName(""); setDesc(""); await onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggle = async (s) => {
    try { await api.put(`/admin/comm/subcategories/${s.key}`, { category_key: category.key, name: s.name, description: s.description || "", active: !s.active }); await onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete subcategory "${s.name}"?`)) return;
    try { await api.delete(`/admin/comm/subcategories/${s.key}`); toast.success("Deleted."); await onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={`Subcategories — ${category.name}`} onClose={onClose} wide>
      <div className="space-y-4" data-testid="subcategory-modal">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div><label className="text-sm text-slate-700">Name</label><input data-testid="sub-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></div>
          <div><label className="text-sm text-slate-700">Description</label><input value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} /></div>
          <Button data-testid="sub-add" onClick={add} disabled={saving} className="bg-amber-500 text-black hover:bg-amber-400 h-10">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</Button>
        </div>
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
          {category.subcategories.map((s) => (
            <div key={s.key} className="flex items-center gap-3 px-4 py-2.5">
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800">{s.name}</p><p className="text-xs text-slate-400 truncate">{s.description || "—"} · {s.template_count} template(s)</p></div>
              <button onClick={() => toggle(s)} className={`text-xs font-semibold ${s.active ? "text-emerald-600" : "text-slate-400"}`}>{s.active ? "Active" : "Inactive"}</button>
              <button onClick={() => del(s)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex justify-end"><Button variant="ghost" onClick={onClose} className="text-slate-600">Done</Button></div>
      </div>
    </Modal>
  );
}

function RuleModal({ rule, categories, onClose, onSaved }) {
  const [mode, setMode] = useState(rule.category_mode);
  const [fixed, setFixed] = useState(rule.fixed_category_key || "");
  const [allowed, setAllowed] = useState(rule.allowed_category_keys || []);
  const [trigger, setTrigger] = useState(rule.trigger_description || "");
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setAllowed((a) => a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/comm/scenario-rules/${rule.stage}`, {
        category_mode: mode, fixed_category_key: mode === "fixed" ? (fixed || null) : null,
        allowed_category_keys: allowed, trigger_description: trigger, active: true,
      });
      toast.success("Scenario rule updated."); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Scenario Rule — ${rule.stage_label}`} onClose={onClose} wide>
      <div className="space-y-4" data-testid="rule-modal">
        <div><label className="text-sm text-slate-700">Trigger description</label>
          <input value={trigger} onChange={(e) => setTrigger(e.target.value)} className={inputCls} /></div>
        <div><label className="text-sm text-slate-700">Category Mode</label>
          <select data-testid="rule-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls}>
            <option value="fixed">Fixed — one locked category, auto-selected</option>
            <option value="select">Selectable — candidate chooses from allowed categories</option>
          </select></div>
        {mode === "fixed" && (
          <div><label className="text-sm text-slate-700">Fixed Category</label>
            <select value={fixed} onChange={(e) => setFixed(e.target.value)} className={inputCls}>
              <option value="">— select —</option>
              {categories.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
            </select></div>
        )}
        <div><label className="text-sm text-slate-700">Allowed Categories</label>
          <div className="flex flex-wrap gap-2 mt-1.5" data-testid="rule-allowed">
            {categories.map((c) => (
              <button key={c.key} onClick={() => toggle(c.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${allowed.includes(c.key) ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}>{c.name}</button>
            ))}
          </div></div>
        <div className="flex justify-end gap-3"><Button variant="ghost" onClick={onClose} className="text-slate-600">Cancel</Button>
          <Button data-testid="rule-save" onClick={save} disabled={saving} className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-6">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Rule"}</Button></div>
      </div>
    </Modal>
  );
}
